import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string;
}
const faqs: FAQItem[] = [{
  question: 'How long until I see results?',
  answer: 'Most men notice reduced shedding within 4-6 weeks. Visible regrowth typically begins around 3-4 months with consistent daily use. Peak results occur at 6-12 months.'
}, {
  question: 'Is this FDA cleared?',
  answer: 'Yes, the red light technology used in CrownControl™ is FDA-cleared for hair growth. Our device uses the clinically proven 650nm wavelength.'
}, {
  question: 'Does it work for receding hairlines?',
  answer: 'Yes, CrownControl™ is effective for both crown thinning and receding hairlines. The 272 LEDs provide full scalp coverage for comprehensive treatment.'
}, {
  question: 'Can I use it daily?',
  answer: 'Yes, we recommend using CrownControl™ once daily for 10 minutes. The device has an auto-timer that shuts off after the optimal treatment duration.'
}, {
  question: "What if it doesn't work?",
  answer: "We offer a 180-day money-back guarantee. If you're not satisfied with your results, return it for a full refund, no questions asked."
}];
export function ProductSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="w-full bg-gray-50 py-20 px-4" data-id="element-169">
      <div className="max-w-5xl mx-auto" data-id="element-170">
        {/* Product Headline */}
        <div className="text-center mb-12" data-id="element-171">
          <h2 className="text-4xl md:text-5xl font-extrabold text-brand-black mb-4" data-id="element-172">
            Meet the CrownControl™ Pro
          </h2>
          <p className="text-xl text-gray-700" data-id="element-173">
            Medical-grade technology designed for maximum results
          </p>
        </div>

        {/* Product Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16" data-id="element-174">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12" data-id="element-175">
            {/* Product Image Placeholder */}
            <div className="bg-gray-100 rounded-xl overflow-hidden" data-id="element-176">
              <img src="https://cdn.magicpatterns.com/uploads/seYq5SeHmyVhebT1ig1yeV/ChatGPT_Image_Feb_19,_2026,_03_27_07_PM.png" alt="CrownControl Pro red light therapy hat showing 272 medical-grade LEDs" className="w-full h-auto" data-id="element-177" />
            </div>

            {/* Features & Pricing */}
            <div data-id="element-178">
              <h3 className="text-2xl font-bold text-brand-black mb-6" data-id="element-179">
                Key Features
              </h3>
              <ul className="space-y-4 mb-8" role="list" data-id="element-180">
                <li className="flex items-start" data-id="element-181">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-182" />
                  <span className="text-gray-700 text-lg" data-id="element-183">
                    <strong data-id="element-184">272 Medical-Grade LEDs</strong> for full scalp
                    coverage
                  </span>
                </li>
                <li className="flex items-start" data-id="element-185">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-186" />
                  <span className="text-gray-700 text-lg" data-id="element-187">
                    <strong data-id="element-188">650nm Red Light</strong> clinically proven
                    wavelength
                  </span>
                </li>
                <li className="flex items-start" data-id="element-189">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-190" />
                  <span className="text-gray-700 text-lg" data-id="element-191">
                    <strong data-id="element-192">Hands-Free Hat Design</strong> wear while you work
                  </span>
                </li>
                <li className="flex items-start" data-id="element-193">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-194" />
                  <span className="text-gray-700 text-lg" data-id="element-195">
                    <strong data-id="element-196">USB Rechargeable</strong> no batteries needed
                  </span>
                </li>
                <li className="flex items-start" data-id="element-197">
                  <CheckIcon className="w-6 h-6 text-brand-red mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-198" />
                  <span className="text-gray-700 text-lg" data-id="element-199">
                    <strong data-id="element-200">Auto 10-Minute Timer</strong> optimal treatment
                    duration
                  </span>
                </li>
              </ul>

              {/* Pricing */}
              <div className="mb-6" data-id="element-201">
                <div className="flex items-baseline gap-3 mb-2" data-id="element-202">
                  <span className="text-gray-500 line-through text-2xl" data-id="element-203">
                    $599
                  </span>
                  <span className="text-brand-red text-5xl font-extrabold" data-id="element-204">
                    $497
                  </span>
                </div>
                <p className="text-gray-600" data-id="element-205">
                  One-time payment. No subscriptions.
                </p>
              </div>

              {/* CTA Button */}
              <a href="https://offer.crowncontrol.shop/" className="block w-full bg-brand-red text-white px-8 py-5 rounded-lg font-bold text-xl hover:bg-red-700 transition-colors shadow-lg text-center" aria-label="Get CrownControl Pro" data-id="element-206">
                Learn More
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div data-id="element-207">
          <h3 className="text-3xl font-bold text-brand-black mb-8 text-center" data-id="element-208">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4" data-id="element-209">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden" data-id="element-210">
                <button onClick={() => toggleFAQ(index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" aria-expanded={openIndex === index} aria-controls={`faq-answer-${index}`} data-id="element-211">
                  <span className="text-lg font-semibold text-brand-black pr-4" data-id="element-212">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-6 h-6 text-gray-600 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-213" />
                </button>
                {openIndex === index && <div id={`faq-answer-${index}`} className="px-6 pb-5 text-gray-700 leading-relaxed" data-id="element-214">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}